import { handleGetUserAppointments } from "../../shared/controllers/generalController";
import { ApiResponse, Game, User } from "../../shared/types";
import { userApi as api, userSilentApi as silentApi } from "../../utils/apiInstances";
import { toIso3 } from "../../utils/locationStorage";
import config from "../config";

/**
 * Function to get user games with optional filtering params
 */
export const handleGetUserGames = async (params: Record<string, unknown> = {}): Promise<ApiResponse<Game[]> | undefined> => {
    try {
        const queryString = Object.keys(params)
            .filter(key => params[key] !== undefined && params[key] !== null && params[key] !== '')
            .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(String(params[key]))}`)
            .join("&");

        const uri = queryString ? `/user/game?${queryString}` : "/user/game";
        const response = await silentApi<Game[]>("GET", uri);
        return response as ApiResponse<Game[]>;
    } catch (error) {
        console.error("Error fetching user games:", error);
    }
    return undefined;
};



/**
 * Get booked games from appointments
 */
export const handleGetUserBookedGames = async (params: Record<string, unknown> = {}): Promise<ApiResponse<Game[]> | undefined> => {
    try {
        const appointmentsResponse = await handleGetUserAppointments<unknown[]>({
            ...params,
            model: "Game",
            status: (params?.status as string) ?? "booked",
        });

        if (appointmentsResponse?.data && Array.isArray(appointmentsResponse.data)) {
            const allowedStatuses = new Set(['booked', 'checked', 'checked in', 'checked_in', 'checked-in', 'completed']);

            const transformedData = (appointmentsResponse.data as Array<Record<string, unknown>>)
                .filter((appointment) => {
                    const rawType = (appointment.appointmentable_type as string) || (appointment.appointmentable as Record<string, unknown>)?.appointmentable_type || (appointment.appointmentable as Record<string, unknown>)?.type || '';
                    const normalizedType = typeof rawType === 'string' ? rawType.split('\\').pop() : '';
                    const isGame = normalizedType === 'Game';
                    const statusNormalized = (typeof appointment.status === 'string' ? appointment.status : '').trim().toLowerCase();
                    const isAllowedStatus = statusNormalized && allowedStatuses.has(statusNormalized);
                    return isGame && isAllowedStatus;
                })
                .map((appointment) => {
                    const game = (appointment.appointmentable as Record<string, unknown>) || {};
                    const playersNeeded = (() => {
                        const candidates = [
                            game.players_needed,
                            game.capacity,
                            game.player_limit,
                            game.total_players,
                        ];
                        for (const candidate of candidates) {
                            const value = Number(candidate);
                            if (Number.isFinite(value) && value >= 0) {
                                return value;
                            }
                        }
                        return 0;
                    })();

                    const confirmedPlayers = (() => {
                        const candidates = [
                            game.confirmed_players,
                            game.players_confirmed,
                            game.confirmed_players_count,
                            game.booked_count,
                            game.participants_count,
                        ]
                            .map(Number)
                            .filter((value) => Number.isFinite(value) && value >= 0);

                        if (candidates.length > 0) {
                            return candidates[0] ?? 0;
                        }

                        if (Array.isArray(game.users)) {
                            return game.users.length;
                        }

                        return 0;
                    })();

                    const remainingPlayers = (() => {
                        if (game.players_remaining !== undefined && game.players_remaining !== null) {
                            const value = Number(game.players_remaining);
                            if (Number.isFinite(value)) {
                                return Math.max(value, 0);
                            }
                        }

                        if (playersNeeded > 0) {
                            return Math.max(playersNeeded - confirmedPlayers, 0);
                        }

                        return null;
                    })();

                    return {
                        ...game,
                        game: game,
                        id: (game.id as string | number) || (appointment.id as string | number),
                        is_participating: true,
                        has_active_booking: true,
                        joined_status: appointment.status as string,
                        appointment_id: appointment.id as string | number,
                        appointment_status: appointment.status as string,
                        appointment_date: appointment.date as string,
                        appointment_start_time: appointment.start_time as string,
                        appointment_end_time: appointment.end_time as string,
                        appointment_price: appointment.price as string | number,
                        appointment_created_at: appointment.created_at as string,
                        date: (game.date as string) || (appointment.date as string),
                        start_time: (game.start_time as string) || (appointment.start_time as string),
                        end_time: (game.end_time as string) || (appointment.end_time as string),
                        price: (game.price as string | number) || (appointment.price as string | number),
                        players_needed: playersNeeded,
                        confirmed_players: confirmedPlayers,
                        players_remaining: remainingPlayers,
                        title: (game.title as string) || "Untitled Game",
                    } as Game;
                });

            return {
                ...appointmentsResponse,
                data: transformedData
            } as ApiResponse<Game[]>;
        }

        return appointmentsResponse as ApiResponse<Game[]>;
    } catch (error) {
        console.error("Error fetching booked games from appointments:", error);
        throw error;
    }
};

/**
 * Get booked pitches from appointments
 */
export const handleGetUserBookedPitches = async (params: Record<string, unknown> = {}): Promise<ApiResponse<Game[]> | undefined> => {
    try {
        const appointmentsResponse = await handleGetUserAppointments<unknown[]>({
            ...params,
            model: "Pitch",
            status: (params?.status as string) ?? "booked",
            include: "appointmentable,appointmentable.media", // Load venue details and images
        });

        if (appointmentsResponse?.data && Array.isArray(appointmentsResponse.data)) {
            const allowedStatuses = new Set(['booked', 'checked', 'checked in', 'checked_in', 'checked-in', 'completed']);

            const transformedData = (appointmentsResponse.data as Array<Record<string, unknown>>)
                .filter((appointment) => {
                    const rawType = (appointment.appointmentable_type as string) || (appointment.appointmentable as Record<string, unknown>)?.appointmentable_type || (appointment.appointmentable as Record<string, unknown>)?.type || '';
                    const normalizedType = typeof rawType === 'string' ? rawType.split('\\').pop() : '';
                    const isPitch = normalizedType === 'Pitch';
                    const statusNormalized = (typeof appointment.status === 'string' ? appointment.status : '').trim().toLowerCase();
                    const isAllowedStatus = statusNormalized && allowedStatuses.has(statusNormalized);
                    return isPitch && isAllowedStatus;
                })
                .map((appointment) => {
                    const pitch = (appointment.appointmentable as Record<string, unknown>) || {};
                    return {
                        ...pitch,
                        pitch: pitch,
                        venue_name: pitch.name as string,
                        venue_location: pitch.location as string,
                        id: (pitch.id as string | number) || (appointment.id as string | number),
                        is_participating: true,
                        has_active_booking: true,
                        joined_status: appointment.status as string,
                        appointment_id: appointment.id as string | number,
                        appointment_status: appointment.status as string,
                        appointment_date: appointment.date as string,
                        appointment_start_time: appointment.start_time as string,
                        appointment_end_time: appointment.end_time as string,
                        appointment_price: appointment.price as string | number,
                        appointment_created_at: appointment.created_at as string,
                        date: (pitch.date as string) || (appointment.date as string),
                        start_time: (pitch.start_time as string) || (appointment.start_time as string),
                        end_time: (pitch.end_time as string) || (appointment.end_time as string),
                        price: (pitch.price as string | number) || (appointment.price as string | number),
                        booking_date: appointment.date as string,
                        booking_start_time: appointment.start_time as string,
                        booking_end_time: appointment.end_time as string,
                        title: (pitch.name as string) || "Booked Pitch",
                    } as unknown as Game;
                });

            return {
                ...appointmentsResponse,
                data: transformedData
            } as ApiResponse<Game[]>;
        }

        return appointmentsResponse as ApiResponse<Game[]>;
    } catch (error) {
        console.error("Error fetching booked pitches from appointments:", error);
        throw error;
    }
};

/**
 * Fetch publicly shared games with optional query params (country/state/city etc.)
 */
export const handleGetPublicGames = async <TData = Game[]>(params: Record<string, unknown> = {}): Promise<ApiResponse<TData> | undefined> => {
    try {
        const normalizedParams = Object.entries(params).reduce(
            (acc: Record<string, unknown>, [key, value]) => {
                if (value === undefined || value === null || value === "") {
                    return acc;
                }

                if (key === "country_code") {
                    if (typeof value !== 'string') return acc;
                    const iso3 = toIso3(value);
                    if (!iso3) {
                        return acc;
                    }
                    acc[key] = iso3;
                    return acc;
                }

                acc[key] = value;
                return acc;
            },
            {}
        );

        const query = Object.entries(normalizedParams)
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
            .join("&");

        const endpoint = query ? `/user/public-game?${query}` : "/user/public-game";
        const response = await silentApi<TData>("GET", endpoint);
        return response as ApiResponse<TData>;
    } catch (error) {
        console.error("Error fetching public games:", error);
    }
    return undefined;
};

interface LocationHeaderContext {
    countryCode?: string | null;
    state?: string | null;
    city?: string | null;
}

/**
 * Function to get match by ID with optional location context (for shared links)
 */
export const handleGetPublicGameById = async (matchID: string | number, locationContext: LocationHeaderContext | null = null): Promise<ApiResponse<Game>> => {
    try {
        const url = `${config.baseURL}/user/public-game/${matchID}`;
        const headers: Record<string, string> = {
            Accept: "application/json",
            "Content-Type": "application/json",
        };

        if (typeof globalThis !== "undefined") {
            const token = globalThis.localStorage.getItem("saca_token");
            if (token) {
                headers["Authorization"] = `Bearer ${token}`;
            }
        }

        if (typeof locationContext?.countryCode === "string" && locationContext.countryCode.trim()) {
            headers["X-Country-Code"] = locationContext.countryCode;
        }
        if (typeof locationContext?.state === "string" && locationContext.state.trim()) {
            headers["X-State"] = locationContext.state;
        }
        if (typeof locationContext?.city === "string" && locationContext.city.trim()) {
            headers["X-City"] = locationContext.city;
        }

        const response = await fetch(url, {
            method: "GET",
            headers,
        });

        const data = await response.json();
        if (!response.ok) {
            throw new Error(data?.message || "Failed to fetch game");
        }
        return data as ApiResponse<Game>;
    } catch (error) {
        console.error("Error fetching public game:", error);
        throw error;
    }
};

/**
 * Function to get users
 */
export const handleGetUsersForGames = async (search: string): Promise<ApiResponse<User[]> | undefined> => {
    try {
        const query = encodeURIComponent(search.trim());
        const response = await silentApi<User[]>("GET", `/user/users?search=${query}`);
        return response as ApiResponse<User[]>;
    } catch (error) {
        console.error("Error fetching users for games:", error);
    }
    return undefined;
};

/**
 * Function to get match by ID
 */
export const handleGetGameById = async (gameId: string | number): Promise<ApiResponse<Game>> => {
    try {
        const response = await silentApi<Game>("GET", `/user/game/${gameId}`);
        return response as ApiResponse<Game>;
    } catch (error) {
        console.error(`Error fetching game by ID ${gameId}:`, error);
        throw error;
    }
};

/**
 * Get game rating for a match
 */
export const handleUserGetGameRating = async (matchID: string | number): Promise<ApiResponse> => {
    try {
        const response = await silentApi("GET", `/user/games/${matchID}/ratings`);
        return response as ApiResponse;
    } catch (error) {
        console.error(`Error fetching game rating for ${matchID}:`, error);
        throw error;
    }
};

/**
 * Rate a game
 */
export const handleUserRateGame = async (matchID: string | number, payload: unknown): Promise<ApiResponse> => {
    try {
        const response = await api("POST", `/user/games/${matchID}/ratings`, payload, { _silent: true });
        return response as ApiResponse;
    } catch (error) {
        console.error(`Error rating game ${matchID}:`, error);
        throw error;
    }
};

/**
 * Book a game
 */
export const handleUserBookGame = async (userData: Record<string, unknown>, onSuccess: (res: ApiResponse) => void, onError?: (err: unknown) => void): Promise<void> => {
    try {
        if (!userData?.type) {
            throw new Error('Required type field is missing');
        }

        const response = await api("POST", "/user/game", userData);
        onSuccess(response as ApiResponse);
    } catch (error) {
        if (onError) {
            onError(error);
        }
    }
};

/**
 * Request check-in
 */
export const handleUserCheckinRequest = async (
    userData: unknown,
    onSuccess: (res: ApiResponse) => void,
    onError?: (err: unknown) => void
): Promise<void> => {
    try {
        const response = await api("POST", "/user/public-checkin", userData);
        onSuccess(response as ApiResponse);
    } catch (error) {
        if (onError) {
            onError(error);
        }
    }
};

/**
 * Update match check-in status
 */
export const handleUserMatchCheckin = async (
    matchID: string | number,
    userData: unknown,
    onSuccess: (res: ApiResponse) => void,
    onError?: (err: unknown) => void
): Promise<void> => {
    try {
        const response = await api(
            "PATCH",
            `/user/public-checkin/${matchID}`,
            userData
        );
        onSuccess(response as ApiResponse);
    } catch (error) {
        if (onError) {
            onError(error);
        }
    }
};

/**
 * Cancel appointment
 */
export const handleUserCancelBooking = async (
    matchID: string | number,
    userData: unknown,
    onSuccess: (res: ApiResponse) => void,
    onError?: (err: unknown) => void
): Promise<void> => {
    try {
        const response = await api(
            "PATCH",
            `/user/cancel-appointments/${matchID}`,
            userData
        );
        onSuccess(response as ApiResponse);
    } catch (error) {
        if (onError) {
            onError(error);
        }
    }
};

/**
 * Handle third party checkin request
 */
export const handleGetThirdPartyUserCheckinRequest = handleUserCheckinRequest;

/**
 * Match verification (e.g. from email link)
 */
export const handleMatchVerification = async (
    id: string | number,
    onSuccess: (res: ApiResponse) => void,
    onError?: (err: unknown) => void
): Promise<void> => {
    try {
        const response = await silentApi("GET", `/user/public-game/${id}`);
        onSuccess(response as ApiResponse);
    } catch (error) {
        console.error("Match verification failed:", error);
        if (onError) {
            onError(error);
        }
    }
};
